import Immutable from 'immutable';
import { replaceReferenceCitationWithinComponents } from 'modules/References/services/replaceReferenceCitationsWithinComponents';
import { batchActions } from 'redux-batched-actions';
import { CallEffect, PutEffect, call, put, select, take } from 'redux-saga/effects';
import guid from 'uuid';
import { DocumentSavingStatus, EntityType, ModalType } from 'const';
import { unlockProjectContent } from 'containers/App/actions';
import { hideModal, showModal } from 'containers/ModalManager/actions';
import * as ModalManagerModels from 'containers/ModalManager/models';
import { deleteUploadingDocumentId } from 'containers/Project/actions';
import * as Models from 'models';
import { updateReferenceCitationDocument as updateReferenceCitationDocumentRequest } from 'services/api';
import { handleSagaError } from 'services/handleError';
import { takeModalWindowResponse } from 'utils/takeModalWindowResponse';
import { addReferenceCitation as addReferenceCitationAction, setDocument } from '../actions';
import { Action } from '../models';
import { citationsForAssetsPanel as citationsSelector } from '../selectors';
import { updateEditedDocument } from '../services/updateEditedDocument';
import { addReferenceCitation } from './addReferenceCitation';

function createUpdateDocumentHandler(referenceToUpdate: Models.ReferenceCitationMap) {
  return function* updateDocument(): Generator<CallEffect | PutEffect> {
    const response = (yield call(
      updateReferenceCitationDocumentRequest,
      referenceToUpdate.get('documentId'),
      referenceToUpdate.toJS() as Models.ReferenceCitation,
    )) as ReturnTypeSaga<typeof updateReferenceCitationDocumentRequest>;

    const createdDocument = (response as Models.SuccessResponse<Models.ReferenceCitation>).data;
    createdDocument.id = referenceToUpdate.get('id');
    createdDocument.documentSource = referenceToUpdate.get('documentSource').toJS();
    createdDocument.text = referenceToUpdate.get('text');

    yield put(setDocument(createdDocument));
  };
}

function createReplaceDocumentHandler(documentIdsToUpdate: Immutable.List<string>, oldReferenceId: string) {
  return function* replaceDocument(uploadingDocumentId: string): Generator<CallEffect> {
    yield call(
      replaceReferenceCitationWithinComponents,
      documentIdsToUpdate,
      oldReferenceId,
      uploadingDocumentId,
    );
  };
}

export function* updateReferenceRequest(action: Action.IUpdateReferenceRequest): Generator {
  const documentSaveWindowId = guid();
  let uploadingDocumentId = guid();

  try {
    const { id, referenceText, documentIdsToUpdate } = action.payload;
    const referenceCitations: ReturnTypeSaga<typeof citationsSelector> = yield select(citationsSelector);
    const referenceCitation = referenceCitations.get(id);
    const updatedReferenceCitation = referenceCitation.set('text', referenceText);

    yield put(showModal(ModalType.DOCUMENT_SAVE, { entityType: EntityType.REFERENCE_CITATION, documentId: id }, documentSaveWindowId));

    const { payload: { response: actionType } }: ModalManagerModels.Action.IHideModal<DocumentSavingStatus> = yield take(
      takeModalWindowResponse(documentSaveWindowId),
    );

    // TODO: use batch actions update
    switch (actionType) {
      case DocumentSavingStatus.CREATE:
        const newReferenceId: string = yield* addReferenceCitation(addReferenceCitationAction(documentSaveWindowId, referenceText));

        if (!newReferenceId) {
          return;
        }

        yield call(replaceReferenceCitationWithinComponents, documentIdsToUpdate, id, newReferenceId);

        return;
      case DocumentSavingStatus.EDIT:
        uploadingDocumentId = yield call(
          updateEditedDocument,
          id,
          referenceText,
          createUpdateDocumentHandler(updatedReferenceCitation),
          createReplaceDocumentHandler(documentIdsToUpdate, id),
        );

        return;
      default: return;
    }
  } catch (error) {
    yield call(handleSagaError, error, 'Documents.updateReferenceRequest', 'UpdateReferenceCitation');
  } finally {
    yield put(batchActions([
      unlockProjectContent(),
      hideModal(documentSaveWindowId),
      deleteUploadingDocumentId(uploadingDocumentId),
    ]));
  }
}
